export interface CsvParseResult {
  columns: string[];
  rows: Record<string, unknown>[];
  rowCount: number;
  errors: Array<{ row: number; message: string }>;
}

const parseCsvLine = (line: string): string[] => {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const next = line[i + 1];
    if (char === '"') {
      if (inQuotes && next === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  result.push(current.trim());
  return result;
};

const inferValue = (value: string): unknown => {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  if (trimmed.toLowerCase() === 'true') return true;
  if (trimmed.toLowerCase() === 'false') return false;
  const numeric = Number(trimmed);
  if (!Number.isNaN(numeric) && trimmed !== '') return numeric;
  return trimmed;
};

export class CsvParserService {
  parse(content: string): CsvParseResult {
    const errors: Array<{ row: number; message: string }> = [];
    const lines = content
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .split('\n')
      .filter((line) => line.trim().length > 0);

    if (lines.length === 0) {
      return { columns: [], rows: [], rowCount: 0, errors: [] };
    }

    const rawColumns = parseCsvLine(lines[0]);
    const columns = rawColumns.map((c) => c.trim()).filter((c) => c.length > 0);

    if (columns.length === 0) {
      errors.push({ row: 0, message: 'CSV header is empty' });
      return { columns: [], rows: [], rowCount: 0, errors };
    }

    const rows: Record<string, unknown>[] = [];
    for (let i = 1; i < lines.length; i++) {
      const values = parseCsvLine(lines[i]);
      if (values.length !== rawColumns.length) {
        errors.push({ row: i, message: `Column count mismatch: expected ${rawColumns.length}, got ${values.length}` });
        if (values.length < rawColumns.length) {
          while (values.length < rawColumns.length) values.push('');
        }
      }
      const row: Record<string, unknown> = {};
      for (let j = 0; j < columns.length; j++) {
        row[columns[j]] = inferValue(values[j] ?? '');
      }
      rows.push(row);
    }

    return { columns, rows, rowCount: rows.length, errors };
  }
}

export const createCsvParserService = (): CsvParserService => new CsvParserService();
